import { pantryColors } from '@pantry/ui'
import React from 'react'
import type { LoyaltySegment } from '../../../../types/loyaltySegments'

const p = pantryColors

// A segment whose LAST evaluation failed: `member_count` (and every aggregate
// next to it) is the last good value, not a current one, and `last_evaluated_at`
// is no proof of freshness. The server's own error string is an internal
// criteria diagnostic — never shown here; the operator gets the consequence.
export const isSegmentCountStale = (segment: {
  evaluation_error?: string | null
}): boolean => Boolean(segment.evaluation_error)

export const segmentsWithStaleCount = (
  segments: LoyaltySegment[],
): LoyaltySegment[] => segments.filter(isSegmentCountStale)

export const SEGMENT_STALE_COUNT_MESSAGE =
  'This segment could not be re-evaluated, so its customer count may be out of date — a campaign sent to it may reach a different set of customers.'

// Same amber informational treatment as the campaign builder's locked/missing
// notes (`NoteBanner`) — used on the segment detail page and the builder's
// audience step where the stale figure is presented as authoritative.
export const SegmentStaleBanner: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => (
  <div
    style={{
      background: p.amber[50],
      border: `1px solid ${p.amber[200]}`,
      color: p.amber[800],
      borderRadius: 10,
      padding: '12px 16px',
      fontSize: 13,
      lineHeight: 1.5,
    }}
  >
    {children ?? SEGMENT_STALE_COUNT_MESSAGE}
  </div>
)

// Compact annotation for list surfaces (segments grid card, segments table
// row, the builder's audience picker) — the same amber-on-nothing treatment
// the campaigns list uses for its "N failed" note under a status pill.
export const SegmentStaleFlag: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => (
  <span
    title={SEGMENT_STALE_COUNT_MESSAGE}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      color: p.amber[700],
      fontSize: compact ? 10.5 : 11.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    <svg
      width={compact ? 11 : 12}
      height={compact ? 11 : 12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
    Count may be out of date
  </span>
)
